import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  TrendingUp,
  Heart,
  Activity,
  Zap,
  Shield,
  AlertTriangle,
} from "lucide-react";

const getScoreColor = (score) => {
  if (score >= 80)
    return { color: "text-green-600", bg: "bg-green-100", icon: Shield };
  if (score >= 60)
    return { color: "text-blue-600", bg: "bg-blue-100", icon: Heart };
  return { color: "text-orange-600", bg: "bg-orange-100", icon: AlertTriangle };
};

const categoryIcons = {
  metabolic_health: Activity,
  cardiovascular: Heart,
  inflammation: Zap,
  hormonal_balance: TrendingUp,
  nutritional_status: Shield,
};

export default function HealthScoreCard({ healthScore, isLoading }) {
  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200 shadow-xl">
        <CardContent className="p-8 text-center">
          <div className="animate-pulse">
            <div className="w-32 h-32 bg-blue-200 rounded-full mx-auto mb-4"></div>
            <div className="h-6 bg-blue-200 rounded w-48 mx-auto"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!healthScore) return null;

  const scoreStyle = getScoreColor(healthScore.overall_score);

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {/* Overall Score */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200 shadow-xl">
        <CardContent className="p-8 text-center">
          <div
            className={`w-32 h-32 ${scoreStyle.bg} rounded-full mx-auto mb-4 flex items-center justify-center`}
          >
            <div>
              <div className={`text-4xl font-bold ${scoreStyle.color}`}>
                {healthScore.overall_score}
              </div>
              <div className="text-sm text-slate-600">/ 100</div>
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Overall Health Score
          </h3>
          <p className="text-slate-600 text-sm">
            {healthScore.overall_score >= 80 && "Excellent health status"}
            {healthScore.overall_score >= 60 &&
              healthScore.overall_score < 80 &&
              "Good health with room for improvement"}
            {healthScore.overall_score < 60 &&
              "Areas requiring attention identified"}
          </p>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <CardTitle className="text-lg">Health Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(healthScore.category_scores || {}).map(
              ([category, score]) => {
                const IconComponent = categoryIcons[category] || Activity;
                const categoryColor = getScoreColor(score);

                return (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <IconComponent
                          className={`w-4 h-4 ${categoryColor.color}`}
                        />
                        <span className="text-sm font-medium capitalize">
                          {category.replace(/_/g, " ")}
                        </span>
                      </div>
                      <span
                        className={`text-sm font-bold ${categoryColor.color}`}
                      >
                        {score}%
                      </span>
                    </div>
                    <Progress value={score} className="h-2" />
                  </div>
                );
              }
            )}
          </div>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <CardTitle className="text-lg">Key Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {healthScore.strengths?.slice(0, 2).map((strength, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-sm text-slate-700">{strength}</p>
              </div>
            ))}

            {healthScore.concerns?.slice(0, 2).map((concern, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-sm text-slate-700">{concern}</p>
              </div>
            ))}
          </div>

          {healthScore.insights && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">{healthScore.insights}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
